// MirrorTab — the "Delta → Mirror" stage of Pipeline Health.
//
// The 14-day SilverM365* ATS event mirror is what the Recent Events page reads AND what
// two alert-path endpoints join against: RecheckSigninOutcome (the "PASSWORD COMPROMISED"
// verdict on Defender/AADIP pass-through alerts, an exact 1:1 requestId join) and
// ResolveDirectoryName (GUID → name for Conditional Access policy edits). That mirror
// silently lost ~10%/day until 2026-08-14 — graph_signin 16.45%, directory_audit 17.81% —
// so roughly 1 in 6 sign-in-outcome re-checks answered "try again in ~30 minutes"
// permanently, because the row was never coming. Nobody was told; someone had to go and
// look. This panel is the standing answer to that.
//
// Everything the panel refuses to do is deliberate, and each refusal is a defect already
// paid for once upstream:
//   * A check that sampled nothing renders "not measured", never "0.0% loss".
//   * The claim is always scoped to the sample ("across N busiest partitions"), never
//     "the mirror is complete" — the producer measures top-K partitions per feed.
//   * The headline is EXCL_DS, not ALL, because device_security is ~81% of the sampled
//     rows and drowns out the feeds analysts actually depend on.
//   * A figure older than ~26h is labelled STALE rather than shown as current.
//   * A loss % over a tiny denominator is marked as noise (risky_user_history read 25%
//     on 2026-08-16 — that is 1 missing row out of 4).
// The server precomputes `measured` / `stale` / `lossUnderstated` / `smallSample` so these
// cannot drift apart from the rendering.

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { fetchSloMirrorCompleteness } from '../../api/client';
import { Section, formatRelativeMinutes } from './sloShared';
// `computedUtc` arrives as PowerShell 'o' format — SEVEN fractional-second digits.
// V8 tolerates that; Firefox and Safari return an Invalid Date. Every render of a
// timestamp here must go through normalizeIsoUtc first, which truncates to
// milliseconds. This bug class recurs — see TIMEZONE_DISPLAY_HISTORY.md.
import { normalizeIsoUtc } from '../../utils/timeFormat';
import Badge from '../ui/Badge';
import { RefreshButton, LoadingCard } from '../ui/LoadingState';

const VERDICT_TONE = {
  ok: 'success',
  watch: 'warning',
  flag: 'critical',
  no_verdict: 'neutral',
};

const VERDICT_LABEL = {
  ok: 'OK',
  watch: 'Watch',
  flag: 'Flag',
  no_verdict: 'Not measured',
};

function fmtNum(n) {
  return Number(n || 0).toLocaleString();
}

// A loss percentage is only ever printed for a row that actually measured something.
// `measured === false` is the "the check did not run" case and must never read as 0%.
function fmtLoss(row) {
  if (!row || !row.measured) return '—';
  const v = Number(row.lossPct);
  if (!Number.isFinite(v)) return '—';
  return `${v.toFixed(2)}%`;
}

function lossColor(row) {
  if (!row || !row.measured) return 'text-gray-500';
  const v = Number(row.lossPct) || 0;
  if (v <= 0) return 'text-emerald-400';
  if (v < 1) return 'text-amber-400';
  return 'text-red-400';
}

export default function MirrorTab({ active, tenantId = '' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await fetchSloMirrorCompleteness({ days: 14 });
      setData(d);
      setError('');
    } catch (err) {
      console.error('[MirrorTab] fetch failed', err);
      setError(err?.message || 'Failed to load mirror completeness data.');
    } finally {
      setLoading(false);
    }
  }, []);

  // One daily row per day — polling would be pointless, so load once on first activation.
  useEffect(() => {
    if (!active || data || loading) return;
    load();
  }, [active, data, loading, load]);

  const latest = data?.latest || null;
  const headline = latest?.exclDs || null;
  const allFeeds = latest?.allFeeds || null;
  const feeds = useMemo(() => (Array.isArray(data?.feeds) ? data.feeds : []), [data]);
  const history = useMemo(() => (Array.isArray(data?.history) ? data.history : []), [data]);

  if (error) {
    return (
      <div className="mb-4 p-4 bg-red-900/30 border border-red-700/50 rounded-lg text-red-300 text-sm flex items-center gap-2">
        <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
        <span>{error}</span>
        <button onClick={load} className="ml-auto underline hover:no-underline text-red-400">Retry</button>
      </div>
    );
  }

  if (loading && !data) return <LoadingCard message="Loading mirror completeness…" />;

  if (data && data.available === false) {
    return (
      <Section title="Delta → ATS event mirror" subtitle="Daily completeness reconciliation">
        <p className="text-sm text-gray-400">
          The mirror-completeness table has not been created yet, so there is nothing to report.
        </p>
        <p className="text-xs text-gray-600 mt-2">
          The pipeline writes <span className="font-mono">SilverM365MirrorCompletenessDaily</span> daily at
          ~14:35&nbsp;UTC for the previous settled day. This panel reads it; it never creates it.
        </p>
      </Section>
    );
  }

  return (
    <div>
      {/* What this number is, and — just as important — what it is not. */}
      <div className="card mb-6 border-l-2 border-l-blue-600/60">
        <div className="flex items-start gap-2">
          <InformationCircleIcon className="h-4 w-4 text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-gray-400 leading-relaxed">
            <p>
              Compares the unified Delta tables against the 14-day ATS event mirror the portal reads —
              the Recent Events page, and the sign-in-outcome re-check and directory-name resolution on
              the alert path. A row present in Delta but missing from the mirror is a row an analyst
              cannot see.
            </p>
            <p className="mt-1.5">
              This is a <strong className="text-gray-300">daily completeness rate over a sample</strong>, not a
              liveness check and not a census. A UTC day does not settle until ~02:00&nbsp;UTC, so measuring
              it more often would manufacture loss that is not there.
              {data?.tenantScoped === false && tenantId ? (
                <span className="text-amber-400">
                  {' '}The tenant filter does not apply to this tab — the reconciliation has no per-tenant grain.
                </span>
              ) : null}
            </p>
          </div>
        </div>
      </div>

      <Section
        title="Latest reconciliation"
        subtitle={
          latest
            ? `Measured ${latest.measuredDate} (the last settled UTC day)`
            : 'No measurement rows in the last 14 days'
        }
        right={<RefreshButton loading={loading} onClick={load} className="btn-secondary flex items-center gap-1.5 text-xs" />}
      >
        {!latest ? (
          <p className="text-sm text-gray-500">
            No reconciliation rows in the window. The producer writes one set of rows per day.
          </p>
        ) : (
          <>
            {latest.stale && (
              <div className="mb-4 p-3 bg-amber-900/25 border border-amber-700/50 rounded text-xs text-amber-300 flex items-start gap-2">
                <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0 mt-px" />
                <span>
                  <strong>Stale.</strong> This result was computed{' '}
                  {headline?.ageHours != null ? `${headline.ageHours}h` : 'more than 26h'} ago, so it is not a
                  current statement about the mirror. The producer should refresh daily at ~14:35&nbsp;UTC —
                  if that keeps slipping, the reconciliation itself has stopped, which is the condition this
                  panel exists to make visible.
                </span>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <DayCard
                row={headline}
                title="Analyst-facing feeds"
                note="Excludes device_security. This is the headline: the feeds identity work depends on."
                primary
              />
              <DayCard
                row={allFeeds}
                title="All mirrored feeds"
                note="Includes device_security, which is ~81% of the sampled rows and dominates this figure."
              />
            </div>

            {headline?.lossUnderstated || allFeeds?.lossUnderstated ? (
              <div className="p-3 bg-amber-900/20 border border-amber-700/40 rounded text-xs text-amber-300">
                Duplicate mirror entities were found ({fmtNum((headline?.excessRows || 0) + (allFeeds?.excessRows || 0))} excess
                rows). Duplicates can mask missing rows, so <strong>the loss figure above is a floor, not the
                true rate.</strong>
              </div>
            ) : null}
          </>
        )}
      </Section>

      {/* Per-feed detail — where a real problem actually shows up. */}
      <Section
        title="By feed"
        subtitle={`${feeds.length} mirrored feed${feeds.length === 1 ? '' : 's'} on the latest measured day`}
        right={
          <button
            onClick={() => setExpanded((v) => !v)}
            className="text-xs text-gray-400 hover:text-gray-200 flex items-center gap-1"
          >
            {expanded ? <ChevronDownIcon className="h-3.5 w-3.5" /> : <ChevronRightIcon className="h-3.5 w-3.5" />}
            {expanded ? 'Hide' : 'Show'}
          </button>
        }
      >
        {!expanded ? null : feeds.length === 0 ? (
          <p className="text-sm text-gray-500">No per-feed rows on the latest measured day.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-800 text-[10px] uppercase tracking-wider text-gray-500">
                  <th className="text-left font-semibold px-2 py-2">Feed</th>
                  <th className="text-left font-semibold px-2 py-2">Verdict</th>
                  <th className="text-right font-semibold px-2 py-2">Delta rows</th>
                  <th className="text-right font-semibold px-2 py-2">Mirror rows</th>
                  <th className="text-right font-semibold px-2 py-2">Missing</th>
                  <th className="text-right font-semibold px-2 py-2">Loss</th>
                  <th className="text-right font-semibold px-2 py-2">Sampled</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800/50">
                {feeds.map((f) => (
                  <tr key={f.rowKey} className="hover:bg-gray-800/30 align-top">
                    <td className="px-2 py-2">
                      <div className="font-mono text-gray-200 text-xs">{f.scope}</div>
                      {f.portalImpact && (
                        <div className="text-[11px] text-gray-500 mt-0.5 max-w-xl leading-snug">{f.portalImpact}</div>
                      )}
                    </td>
                    <td className="px-2 py-2">
                      <Badge tone={VERDICT_TONE[f.verdict] || 'neutral'}>
                        {VERDICT_LABEL[f.verdict] || f.verdict || '—'}
                      </Badge>
                      {f.smallSample && f.missingRows > 0 && (
                        <div className="text-[11px] text-gray-500 mt-1 max-w-xs leading-snug">
                          Too few rows for the percentage to mean much — {fmtNum(f.missingRows)} of{' '}
                          {fmtNum(f.deltaRows)}.
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-2 text-right text-gray-300 tabular-nums">{fmtNum(f.deltaRows)}</td>
                    <td className="px-2 py-2 text-right text-gray-300 tabular-nums">{fmtNum(f.atsRows)}</td>
                    <td className={`px-2 py-2 text-right tabular-nums ${f.missingRows > 0 ? 'text-red-400' : 'text-gray-500'}`}>
                      {fmtNum(f.missingRows)}
                    </td>
                    <td className={`px-2 py-2 text-right tabular-nums font-medium ${lossColor(f)}`}>{fmtLoss(f)}</td>
                    <td className="px-2 py-2 text-right text-gray-500 text-xs tabular-nums">
                      {f.measured ? `${fmtNum(f.partitionsSampled)} part.` : 'none'}
                      {f.erroredPartitions > 0 && (
                        <span className="text-amber-400" title="Partition queries that failed and are excluded from the rate">
                          {' '}
                          +{fmtNum(f.erroredPartitions)} err
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {feeds[0]?.sampleScope && (
              <p className="text-[11px] text-gray-600 mt-3">
                Sampling: <span className="font-mono">{feeds[0].sampleScope}</span>. Percentages describe the
                sampled partitions only — a feed reading 1–3 partitions is genuinely sparse, not faulty.
              </p>
            )}
          </div>
        )}
      </Section>

      {/* History accrues one row per day; the producer started 2026-08-17. */}
      <Section
        title="Daily history"
        subtitle="Loss rate per measured day — analyst-facing feeds vs. all feeds"
      >
        {history.length === 0 ? (
          <p className="text-sm text-gray-500">No history yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-800 text-[10px] uppercase tracking-wider text-gray-500">
                  <th className="text-left font-semibold px-2 py-2">Measured day</th>
                  <th className="text-left font-semibold px-2 py-2">Verdict (analyst feeds)</th>
                  <th className="text-right font-semibold px-2 py-2">Loss (analyst feeds)</th>
                  <th className="text-right font-semibold px-2 py-2">Loss (all feeds)</th>
                  <th className="text-right font-semibold px-2 py-2">Missing rows</th>
                  <th className="text-right font-semibold px-2 py-2">Computed</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800/50">
                {[...history].reverse().map((h) => {
                  const ex = h.exclDs;
                  const al = h.allFeeds;
                  return (
                    <tr key={h.measuredDate} className="hover:bg-gray-800/30">
                      <td className="px-2 py-2 text-gray-300 font-mono text-xs">{h.measuredDate}</td>
                      <td className="px-2 py-2">
                        <Badge tone={VERDICT_TONE[ex?.verdict] || 'neutral'}>
                          {VERDICT_LABEL[ex?.verdict] || ex?.verdict || '—'}
                        </Badge>
                      </td>
                      <td className={`px-2 py-2 text-right tabular-nums ${lossColor(ex)}`}>{fmtLoss(ex)}</td>
                      <td className={`px-2 py-2 text-right tabular-nums ${lossColor(al)}`}>{fmtLoss(al)}</td>
                      <td className="px-2 py-2 text-right text-gray-400 tabular-nums">
                        {fmtNum((ex?.missingRows || 0))}
                      </td>
                      <td className="px-2 py-2 text-right text-gray-500 text-xs">
                        {formatRelativeMinutes(normalizeIsoUtc(ex?.computedUtc || al?.computedUtc))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {history.length === 1 && (
              <p className="text-[11px] text-gray-600 mt-3">
                One day of history so far — the scheduled producer began writing on 2026-08-17, and a trend
                needs several settled days before it says anything.
              </p>
            )}
          </div>
        )}
      </Section>
    </div>
  );
}

// One of the two day-level rows. Renders "not measured" rather than a reassuring 0%
// whenever the check sampled nothing, which is the whole point of the verdict field.
function DayCard({ row, title, note, primary = false }) {
  if (!row) {
    return (
      <div className="p-4 rounded border border-gray-800 bg-gray-900/40">
        <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">{title}</div>
        <div className="text-2xl font-bold text-gray-600">—</div>
        <div className="text-[11px] text-gray-600 mt-1">Row not present for this day.</div>
      </div>
    );
  }

  const measured = !!row.measured;

  return (
    <div className={`p-4 rounded border ${primary ? 'border-blue-800/60 bg-blue-950/20' : 'border-gray-800 bg-gray-900/40'}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs uppercase tracking-wide text-gray-500">{title}</span>
        <Badge tone={VERDICT_TONE[row.verdict] || 'neutral'}>
          {VERDICT_LABEL[row.verdict] || row.verdict || '—'}
        </Badge>
      </div>

      {measured ? (
        <>
          <div className={`text-3xl font-bold ${lossColor(row)}`}>{fmtLoss(row)}</div>
          <div className="text-[11px] text-gray-500 mt-1">
            row loss across the {fmtNum(row.partitionsSampled)} busiest partitions sampled
          </div>
          <div className="text-xs text-gray-400 mt-2 tabular-nums">
            {fmtNum(row.missingRows)} missing of {fmtNum(row.deltaRows)} Delta rows
            {row.excessRows > 0 && <span className="text-amber-400"> · {fmtNum(row.excessRows)} excess</span>}
          </div>
        </>
      ) : (
        <>
          <div className="text-2xl font-bold text-gray-400">Not measured</div>
          <div className="text-[11px] text-gray-500 mt-1 leading-snug">
            No partitions were sampled, so there is no loss rate for this day — this is not a clean result.
          </div>
        </>
      )}
    </div>
  );
}
